// src/shared/config/langConstants.ts

export type LangCode = 'kor' | 'eng';
export const DEFAULT_LANG: LangCode = 'kor';
type LanguageMap = Record<LangCode, string>;

// The outer record now uses this dynamic LanguageMap type.

export const LANG_KEYS = {
	STATIC_SESSION_DISABLE: 'STATIC_SESSION_DISABLE',
	STATIC_SENDING_DISABLE: 'STATIC_SENDING_DISABLE',
	SESSIONS_WITH_CHARACTER: 'SESSIONS_WITH_CHARACTER',
	CREATE_NEW_PROFILE: 'CREATE_NEW_PROFILE',
	START_NEW_SESSION: 'START_NEW_SESSION',
	CHOOSE_EXISTING_PROFILE: 'CHOOSE_EXISTING_PROFILE',
	CREATE_PROFILE: 'CREATE_PROFILE',
	EDIT_PROFILE: 'EDIT_PROFILE',
	GENDER: 'GENDER',
	MALE: 'MALE',
	FEMALE: 'FEMALE',
	OTHER: 'OTHER',
	ERROR: 'ERROR',
	CHARACTERS: 'CHARACTERS',
	MY_CHARACTERS: 'MY_CHARACTERS',
	LOGOUT: 'LOGOUT',
	EDIT: 'EDIT',
	SEND: 'SEND',
	STORY: 'STORY',
	CANCEL: 'CANCEL',
	LOADING_CHARACTERS: 'LOADING_CHARACTERS',
	LOADING_CHAT: 'LOADING_CHAT',
	LOADING_SESSIONS: 'LOADING_SESSIONS',
	LOADING_STORIES: 'LOADING_STORIES',
	LOADING_STORY: 'LOADING_STORY',
	FAILED_LOAD_CHAT: 'FAILED_LOAD_CHAT',
	CREATING_SESSION: 'CREATING_SESSION',
	GEN_RESPONSE: 'GEN_RESPONSE',
	NEW_CHARACTER: 'NEW_CHARACTER',
	EDIT_CHARACTER: 'EDIT_CHARACTER',
	CHARACTER_CREATED_SUCCESS: 'CHARACTER_CREATED_SUCCESS',
	NO_IMAGES: 'NO_IMAGES',
	PORTRAIT: 'PORTRAIT',
	EMOTION: 'EMOTION',
	BASIC_INFO: 'BASIC_INFO',
	CREATING: 'CREATING',
	CREATE: 'CREATE',
	UPDATE: 'UPDATE',
	UPDATING: 'UPDATING',
	NAME: 'NAME',
	SHOWNAME: 'SHOWNAME',
	DESCRIPTION: 'DESCRIPTION',
	CONTACT: 'CONTACT',
	INSTRUCTION: 'INSTRUCTION',
	TITLE: 'TITLE',
	UPLOAD_IMAGE: 'UPLOAD_IMAGE',
	CHARACTER_DETAIL: 'CHARACTER_DETAIL',
	TITLE_GUIDANCE: 'TITLE_GUIDANCE',
	FIRST_MESSAGE: 'FIRST_MESSAGE',
	NEW_CHARACTER_TITLE: 'NEW_CHARACTER_TITLE',
	NO_SESSIONS: 'NO_SESSIONS',
	NO_PROFILES: 'NO_PROFILES',
	NO_HISTORIES: 'NO_HISTORIES',

	// --- [NEW] Emotion Keys ---
	NEUTRAL: 'NEUTRAL',
	HAPPY: 'HAPPY',
	ANGRY: 'ANGRY',
	SAD: 'SAD',
	FEAR: 'FEAR',
	CURIOUS: 'CURIOUS',
	AFFECTIONATE: 'AFFECTIONATE',
	EXCITED: 'EXCITED',
	DISGUSTED: 'DISGUSTED',
	CONFIDENT: 'CONFIDENT',
	ASHAMED: 'ASHAMED',
	AMAZED: 'AMAZED',
	BORED: 'BORED',
	ROMANTIC: 'ROMANTIC',
	CONFLICTED: 'CONFLICTED',

	INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
	FILE_TOO_LARGE: 'FILE_TOO_LARGE',
	IMAGE_UPLOADED_FOR: 'IMAGE_UPLOADED_FOR',
	FAILED_TO_CREATE_CHARACTER: 'FAILED_TO_CREATE_CHARACTER',
	ERROR_CREATING_CHARACTER: 'ERROR_CREATING_CHARACTER',
	SHOW_NAME_REQUIRED: 'SHOW_NAME_REQUIRED',
	NAME_REQUIRED: 'NAME_REQUIRED',
	GENDER_REQUIRED: 'GENDER_REQUIRED',
	TITLE_REQUIRED: 'TITLE_REQUIRED',
	DESCRIPTION_REQUIRED: 'DESCRIPTION_REQUIRED',
	INSTRUCTION_REQUIRED: 'INSTRUCTION_REQUIRED',
	FIRST_MESSAGE_REQUIRED: 'FIRST_MESSAGE_REQUIRED',
	SHOW_NAME_PLACEHOLDER: 'SHOW_NAME_PLACEHOLDER',
	NAME_PLACEHOLDER: 'NAME_PLACEHOLDER',
	CONTACT_PLACEHOLDER: 'CONTACT_PLACEHOLDER',
	TITLE_PLACEHOLDER: 'TITLE_PLACEHOLDER',
	DESCRIPTION_PLACEHOLDER: 'DESCRIPTION_PLACEHOLDER',
	INSTRUCTION_PLACEHOLDER: 'INSTRUCTION_PLACEHOLDER',
	FIRST_MESSAGE_PLACEHOLDER: 'FIRST_MESSAGE_PLACEHOLDER',
	DESCRIPTION_HELPER: 'DESCRIPTION_HELPER',
	INSTRUCTION_HELPER: 'INSTRUCTION_HELPER',
	FIRST_MESSAGE_HELPER: 'FIRST_MESSAGE_HELPER',
} as const;
export type LangKey = keyof typeof LANG_KEYS;

export type LangRecord = Record<LangKey[number], LanguageMap>;
export const langConstants: LangRecord = {
	SESSIONS_WITH_CHARACTER: { kor: '지난 이야기', eng: 'Past Sessions' },
	CREATE_NEW_PROFILE: { kor: '새 프로필 만들기', eng: 'Create a New Profile' },
	CREATE_PROFILE: { kor: '프로필 생성', eng: 'Create Profile' },
	EDIT_PROFILE: { kor: '프로필 수정', eng: 'Update Profile' },
	CHOOSE_EXISTING_PROFILE: { kor: '기존 프로필', eng: 'Existing Profiles' },
	START_NEW_SESSION: { kor: '새로운 대화 시작하기', eng: 'Start a New Conversation' },
	GENDER: { kor: '성별', eng: 'Gender' },
	MALE: { kor: '남성', eng: 'Male' },
	FEMALE: { kor: '여성', eng: 'Female' },
	OTHER: { kor: '기타', eng: 'Other' },
	ERROR: { kor: '에러', eng: 'Error' },
	CHARACTERS: { kor: '캐릭터', eng: 'Characters' },
	MY_CHARACTERS: { kor: '내 캐릭터', eng: 'My Characters' },
	LOGOUT: { kor: '로그아웃', eng: 'Logout' },
	EDIT: { kor: '수정', eng: 'Edit' },
	SEND: { kor: '보내기', eng: 'Send' },
	TITLE: { kor: '한줄 소개', eng: 'Tagline' },
	STORY: { kor: '스토리', eng: 'Story' },
	CANCEL: { kor: '취소', eng: 'Cancel' },
	CREATING: { kor: '생성중...', eng: 'Creating...' },
	UPDATING: { kor: '수정중...', eng: 'Updating...' },
	CREATE: { kor: '생성', eng: 'Create' },
	UPDATE: { kor: '수정', eng: 'Update' },
	LOADING_CHARACTERS: { kor: '캐릭터를 불러오는 중입니다...', eng: 'Loading characters...' },
	LOADING_SESSIONS: { kor: '지난 이야기를 불러오는 중입니다...', eng: 'Loading past sessions...' },
	LOADING_STORIES: {
		kor: '캐릭터 스토리를 불러오는 중입니다...',
		eng: 'Loading character stories...',
	},
	LOADING_STORY: { kor: '스토리를 불러오는 중입니다...', eng: 'Loading story...' },
	CREATING_SESSION: { kor: '새 이야기를 시작하는 중입니다...', eng: 'Starting a new session...' },
	FAILED_LOAD_CHAT: {
		kor: '채팅을 불러오는 데 실패했습니다. 다시 시도해 주세요.',
		eng: 'Failed to load chat. Please try again.',
	},
	LOADING_CHAT: { kor: '채팅을 불러오는 중입니다...', eng: 'Loading chat...' },
	GEN_RESPONSE: { kor: '답변을 받아오는 중입니다...', eng: 'Generating response...' },
	NEW_CHARACTER: { kor: '새 캐릭터', eng: 'New Character' },
	EDIT_CHARACTER: { kor: '캐릭터 수정', eng: 'Edit Character' },
	NEW_CHARACTER_TITLE: { kor: '새 캐릭터를 만들어봐요!', eng: `Let's make a new character!` },
	NO_IMAGES: { kor: '업로드된 이미지가 없습니다.', eng: 'No images uploaded.' },
	NO_SESSIONS: {
		kor: '지난 이야기가 없습니다. 새 이야기를 시작해봐요!',
		eng: `No sessions found. Let the story begins!`,
	},
	NO_PROFILES: { kor: '기존 프로필이 없습니다.', eng: `No profiles found.` },
	NO_HISTORIES: { kor: '캐릭터의 옛 이야기가 없습니다.', eng: `No character histories found.` },
	PORTRAIT: { kor: '초상화', eng: 'Portraits' },
	EMOTION: { kor: '감정', eng: 'Emotion' },
	BASIC_INFO: { kor: '기본 정보', eng: 'Basic Information' },
	NAME: { kor: '이름(영어)', eng: 'Name (English)' },
	SHOWNAME: { kor: '이름', eng: 'Display Name' },
	DESCRIPTION: { kor: '캐릭터 소개', eng: 'Character Introduction' },
	CONTACT: { kor: '연락처', eng: 'Contact' },
	INSTRUCTION: { kor: '세계관 및 설정', eng: 'World & AI Instructions' },
	UPLOAD_IMAGE: { kor: '이미지 업로드', eng: 'Upload Image' },
	CHARACTER_DETAIL: { kor: '상세 정보', eng: 'Details' },
	TITLE_GUIDANCE: {
		kor: '한줄 소개는 다른 사람들에게 보이는 정보이며, AI 답변에 사용되지 않습니다.',
		eng: 'This tagline is shown to other users and does not affect AI responses.',
	},
	FIRST_MESSAGE: { kor: '첫 메시지', eng: 'First Message' },
	SHOW_NAME_REQUIRED: { kor: '이름은 필수입니다.', eng: 'Display Name is required.' },
	NAME_REQUIRED: {
		kor: '캐릭터 이름(영어)은 필수입니다.',
		eng: 'Character Name (English, for ID) is required.',
	},
	GENDER_REQUIRED: { kor: '성별은 필수입니다.', eng: 'Gender is required.' },
	TITLE_REQUIRED: { kor: '한줄 소개는 필수입니다.', eng: 'Title is required.' },
	DESCRIPTION_REQUIRED: {
		kor: '캐릭터 소개는 필수 항목입니다.',
		eng: 'Character Introduction is required.',
	},
	INSTRUCTION_REQUIRED: {
		kor: '세계관 및 설정은 필수 항목입니다.',
		eng: 'World & AI Instructions are required.',
	},
	FIRST_MESSAGE_REQUIRED: { kor: '첫 메시지는 필수 항목입니다.', eng: 'First Message is required.' },
	SHOW_NAME_PLACEHOLDER: {
		kor: '캐릭터가 표시될 이름을 입력해 주세요.',
		eng: 'Enter the name to be displayed for the character.',
	},
	NAME_PLACEHOLDER: {
		kor: '캐릭터의 영어 이름을 한 단어로 작성해 주세요.',
		eng: "Enter a single English name for the character's ID.",
	},
	CONTACT_PLACEHOLDER: {
		kor: '캐릭터 작성자의 연락처 정보를 기재해 주세요.',
		eng: "Enter the contact information of the character's creator.",
	},
	TITLE_PLACEHOLDER: {
		kor: '다른 유저들에게 캐릭터를 소개해 주세요.',
		eng: 'Introduce your character to other users.',
	},
	DESCRIPTION_PLACEHOLDER: {
		kor: '캐릭터의 이름, 성별, 성격, 외모 등을 자세히 설명해주세요.',
		eng: "Describe the character's name, gender, personality, appearance, etc. in detail.",
	},
	INSTRUCTION_PLACEHOLDER: {
		kor: '캐릭터의 세계관에 대해 설명해주세요.',
		eng: 'Explain the world and setting this character belongs to.',
	},
	FIRST_MESSAGE_PLACEHOLDER: {
		kor: '새 대화를 시작할 때 캐릭터가 보내는 첫 메시지입니다.',
		eng: "The character's first message when starting a new conversation.",
	},
	DESCRIPTION_HELPER: {
		kor: '캐릭터의 자세한 정보를 서술해 주세요. 이 정보는 유저와 AI 양쪽에 제공됩니다.',
		eng: 'This is the character information provided to the AI.',
	},
	INSTRUCTION_HELPER: {
		kor: '캐릭터의 세계관에 대해 서술해 주세요. 이 정보는 유저와 AI 양쪽에 제공됩니다.',
		eng: "This explains the character's world setting to the AI.",
	},
	FIRST_MESSAGE_HELPER: {
		kor: '채팅창에 표시되는 캐릭터의 첫 대화입니다.',
		eng: "This is the character's first line of dialogue shown in the chat window.",
	},

	// --- Emotion Translations ---
	NEUTRAL: { kor: '중립', eng: 'Neutral' },
	HAPPY: { kor: '행복', eng: 'Happy' },
	ANGRY: { kor: '화남', eng: 'Angry' },
	SAD: { kor: '슬픔', eng: 'Sad' },
	FEAR: { kor: '두려움', eng: 'Fear' },
	CURIOUS: { kor: '궁금함', eng: 'Curious' },
	AFFECTIONATE: { kor: '애정', eng: 'Affectionate' },
	EXCITED: { kor: '흥분', eng: 'Excited' },
	DISGUSTED: { kor: '역겨움', eng: 'Disgusted' },
	CONFIDENT: { kor: '자신감', eng: 'Confident' },
	ASHAMED: { kor: '부끄러움', eng: 'Ashamed' },
	AMAZED: { kor: '놀람', eng: 'Amazed' },
	BORED: { kor: '지루함', eng: 'Bored' },
	ROMANTIC: { kor: '낭만적인', eng: 'Romantic' },
	CONFLICTED: { kor: '혼란스러운', eng: 'Conflicted' },
};

export const alertToastConstants: LangRecord = {
	CREATE_NEW_PROFILE: {
		kor: '대화에 참여할 프로필을 생성해 주세요.',
		eng: 'Please create a profile to join the conversation.',
	},
	STATIC_SENDING_DISABLE: {
		kor: '샘플 사이트에서는 메시지를 보내실 수 없습니다.',
		eng: 'Sending messages is disabled on the sample site.',
	},
	STATIC_SESSION_DISABLE: {
		kor: '샘플 사이트에서는 새 대화를 시작하실 수 없습니다.',
		eng: 'Starting a new conversation is disabled on the sample site.',
	},
	CHARACTER_CREATED_SUCCESS: {
		kor: '캐릭터를 생성하였습니다.',
		eng: 'Character created successfully!',
	},
	INVALID_FILE_TYPE: {
		kor: '잘못된 파일 형식입니다. 이미지 파일을 업로드해주세요.',
		eng: 'Invalid file type. Please upload an image file.',
	},
	FILE_TOO_LARGE: {
		kor: '파일이 너무 큽니다. 5MB 미만의 이미지를 업로드해주세요.',
		eng: 'File is too large. Please upload an image smaller than 5MB.',
	},
	IMAGE_UPLOADED_FOR: {
		kor: '감정 이미지가 업로드되었습니다!',
		eng: 'Emotion image has been uploaded!',
	},
	FAILED_TO_CREATE_CHARACTER: {
		kor: '캐릭터 생성에 실패했습니다.',
		eng: 'Failed to create character.',
	},
	ERROR_CREATING_CHARACTER: {
		kor: '캐릭터 생성 중 오류가 발생했습니다. 다시 시도해주세요.',
		eng: 'An error occurred while creating the character. Please try again.',
	},
};
